import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
    ArrowLeft,
    Heart,
    ChevronRight,
    ChevronLeft,
    Save,
    RefreshCw,
    Users,
    User,
    Sparkles,
    Home,
    Eye,
    EyeOff,
    Download,
    Share2,
    Copy,
    Check,
    Loader2,
    Link2,
    UserPlus
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import * as htmlToImage from 'html-to-image';
import { PrimaryButton, SecondaryButton, TertiaryButton, IconButton } from '../components/brand/BrandButton';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import { ModuleHeader } from '../components/brand/QuickExit';
import { PersistentBottomBar, BottomBarSpacer } from '../components/brand/PersistentBottomBar';
import { toast } from 'sonner';

const STORAGE_KEY = 'infinity_loop_data';

// Step definitions with prompts for both relationship types
const STEPS = {
    partner: [
        {
            id: 'A',
            title: 'What I Yearn For',
            prompt: '"I yearn for this….."',
            question: 'What is the most basic thing you want from your partner in this moment? What do you want and/or wish for?',
            example: 'I yearn to feel loved and important.',
            zone: 'under', // under the surface
            position: 'left'
        },
        {
            id: 'B',
            title: 'My Vulnerable Feeling',
            prompt: '"I feel……when what I yearn for does not happen"',
            question: 'What is the primary feeling that comes up when your need/want from Step A is not met?',
            example: "I feel sad and insecure when I perceive you don't love me and I'm not your priority.",
            zone: 'under',
            position: 'left'
        },
        {
            id: 'C',
            title: 'What I Show Instead',
            prompt: '"When my vulnerable feelings are too difficult, I show……instead"',
            question: 'How do you tend to behave to "cover" up thoughts and feelings related to unmet needs/wants?',
            example: 'Rather than communicate my sadness or insecurity, I show up angry instead and create distance from you.',
            zone: 'above', // above the surface
            position: 'left'
        },
        {
            id: 'D',
            title: 'What I Think About Me',
            prompt: '"What I think about me then is……"',
            question: 'What is your perception of yourself during these times?',
            example: "I'm always being taken for granted and I shouldn't make myself available to you.",
            zone: 'above',
            position: 'right'
        },
        {
            id: 'E',
            title: 'What I Think About You',
            prompt: '"What I think about you then is….."',
            question: 'What is your perception of your partner during these times? What feeds the vulnerable feeling?',
            example: "They don't care about me enough to make me a priority.",
            zone: 'above',
            position: 'right'
        },
        {
            id: 'F',
            title: 'What I Do (The Trigger)',
            prompt: '"Then, what I do to take care of myself is……which triggers my partner"',
            question: 'What behavior comes out as a response, that is directed at your partner?',
            example: "I tend to totally shut-down, which my partner doesn't understand, or I pick a fight over something small from our past.",
            zone: 'above',
            position: 'right'
        }
    ],
    family: [
        {
            id: 'A',
            title: 'What I Yearn For',
            prompt: '"I yearn for this….."',
            question: 'What is the most basic thing you want from your child in this moment? What do you want and/or wish for?',
            example: 'I yearn to feel appreciated and valued.',
            zone: 'under',
            position: 'left'
        },
        {
            id: 'B',
            title: 'My Vulnerable Feeling',
            prompt: '"I feel……when what I yearn for does not happen"',
            question: 'What is the primary feeling that comes up when your need/want from Step A is not met?',
            example: "I feel unappreciated, unrecognized, and overlooked when I perceive that they don't see how hard I work to take care of them.",
            zone: 'under',
            position: 'left'
        },
        {
            id: 'C',
            title: 'What I Show Instead',
            prompt: '"When my vulnerable feelings are too difficult I show……Instead"',
            question: 'How do you tend to behave to "cover" up thoughts and feelings related to unmet needs/wants?',
            example: 'Rather than communicate my want to feel appreciated, I show up frustrated and overwhelmed instead, resenting how much I have on my plate.',
            zone: 'above',
            position: 'left'
        },
        {
            id: 'D',
            title: 'What I Think About Me',
            prompt: '"What I think about me then is……"',
            question: 'What is your perception of yourself during these times?',
            example: 'I always going above and beyond for others, and I never ask for help.',
            zone: 'above',
            position: 'right'
        },
        {
            id: 'E',
            title: 'What I Think About Them',
            prompt: '"What I think about you then is….."',
            question: 'What is your perception of your child during these times? What feeds the vulnerable feeling?',
            example: "They're careless and inconsiderate at times, don't even say thank you, and just expect things to be done for them.",
            zone: 'above',
            position: 'right'
        },
        {
            id: 'F',
            title: 'What I Do (The Trigger)',
            prompt: '"Then, what I do to take care of myself is……which triggers my family member"',
            question: 'What behavior comes out as a response, that is directed at your child?',
            example: "Now that I'm overwhelmed, I lash out, pointing out all the areas in their life that I feel they can be doing more in.",
            zone: 'above',
            position: 'right'
        }
    ]
};

// Infinity Loop SVG visualization component
const InfinityLoopVisualization = ({ currentStep, answers, relationshipType }) => {
    const steps = STEPS[relationshipType];
    
    // Position markers on the infinity loop
    const stepPositions = {
        'A': { x: 120, y: 180 },  // Left loop, bottom
        'B': { x: 80, y: 120 },   // Left loop, middle-bottom
        'C': { x: 80, y: 60 },    // Left loop, top
        'D': { x: 220, y: 60 },   // Right loop, top
        'E': { x: 260, y: 120 },  // Right loop, middle-top
        'F': { x: 220, y: 180 }   // Right loop, bottom
    };
    
    return (
        <div className="relative w-full max-w-md mx-auto">
            <svg viewBox="0 0 340 240" className="w-full h-auto">
                {/* Surface line */}
                <line 
                    x1="20" y1="100" x2="320" y2="100" 
                    stroke="#7D8E95" 
                    strokeWidth="1" 
                    strokeDasharray="4,4"
                    opacity="0.5"
                />
                <text x="30" y="90" fill="#7D8E95" fontSize="10" opacity="0.7">Above the Surface</text>
                <text x="30" y="115" fill="#7D8E95" fontSize="10" opacity="0.7">Under the Surface</text>
                
                {/* Infinity loop path */}
                <path
                    d="M170,120 
                       C170,60 80,40 80,100 
                       C80,160 170,180 170,120 
                       C170,60 260,40 260,100 
                       C260,160 170,180 170,120"
                    fill="none"
                    stroke="url(#infinityGradient)"
                    strokeWidth="3"
                    opacity="0.6"
                />
                
                {/* Gradient definition */}
                <defs>
                    <linearGradient id="infinityGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#1D6B6B" />
                        <stop offset="50%" stopColor="#FFBB98" />
                        <stop offset="100%" stopColor="#1D6B6B" />
                    </linearGradient>
                </defs>
                
                {/* Step markers */}
                {steps.map((step, index) => {
                    const pos = stepPositions[step.id];
                    const isCurrentStep = currentStep === index;
                    const hasAnswer = answers[step.id];
                    
                    return (
                        <g key={step.id}>
                            {/* Marker circle */}
                            <circle
                                cx={pos.x}
                                cy={pos.y}
                                r={isCurrentStep ? 18 : 14}
                                fill={isCurrentStep ? '#1D6B6B' : hasAnswer ? '#FFBB98' : '#2A2520'}
                                stroke={isCurrentStep ? '#FFBB98' : hasAnswer ? '#1D6B6B' : '#3A3530'}
                                strokeWidth="2"
                                className="transition-all duration-300"
                            />
                            {/* Step letter */}
                            <text
                                x={pos.x}
                                y={pos.y + 4}
                                textAnchor="middle"
                                fill={isCurrentStep || hasAnswer ? '#FAFAF5' : '#7D8E95'}
                                fontSize={isCurrentStep ? '14' : '12'}
                                fontWeight="600"
                            >
                                {step.id}
                            </text>
                        </g>
                    );
                })}
                
                {/* Direction arrows on the loop */}
                <polygon points="140,70 145,65 145,75" fill="#7D8E95" opacity="0.5" />
                <polygon points="200,70 195,65 195,75" fill="#7D8E95" opacity="0.5" />
                <polygon points="140,170 145,165 145,175" fill="#7D8E95" opacity="0.5" />
                <polygon points="200,170 195,165 195,175" fill="#7D8E95" opacity="0.5" />
            </svg>
            
            {/* Legend */}
            <div className="flex justify-center gap-6 mt-4">
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#1D6B6B]" />
                    <SmallText className="text-[#7D8E95]">Current</SmallText>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#FFBB98]" />
                    <SmallText className="text-[#7D8E95]">Completed</SmallText>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#2A2520] border border-[#3A3530]" />
                    <SmallText className="text-[#7D8E95]">Pending</SmallText>
                </div>
            </div>
        </div>
    );
};

const InfinityLoop = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [phase, setPhase] = useState('intro'); // intro, type-select, step, review, complete, share-created, join, partner-step, comparison
    const [relationshipType, setRelationshipType] = useState(null); // partner or family
    const [currentStep, setCurrentStep] = useState(0);
    const [answers, setAnswers] = useState({});
    const [currentInput, setCurrentInput] = useState('');
    const [showExample, setShowExample] = useState(false);
    const [savedLoops, setSavedLoops] = useState([]);
    const [fadeIn, setFadeIn] = useState(true);
    const [isExporting, setIsExporting] = useState(false);
    const [copied, setCopied] = useState(false);
    
    // Partner sharing state
    const [shareCode, setShareCode] = useState('');
    const [joinCode, setJoinCode] = useState('');
    const [isSharing, setIsSharing] = useState(false);
    const [partnerName, setPartnerName] = useState('');
    const [myName, setMyName] = useState('');
    const [sharedSession, setSharedSession] = useState(null);
    const [codeCopied, setCodeCopied] = useState(false);
    
    // Ref for exportable content
    const exportRef = useRef(null);
    
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    
    // Check for join code in URL on mount
    useEffect(() => {
        const codeFromUrl = searchParams.get('code');
        if (codeFromUrl) {
            setJoinCode(codeFromUrl.toUpperCase());
            checkShareCode(codeFromUrl.toUpperCase());
        }
    }, [searchParams]);
    
    // Load saved data on mount
    useEffect(() => {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            try {
                setSavedLoops(JSON.parse(saved));
            } catch (e) {
                setSavedLoops([]);
            }
        }
    }, []);
    
    const steps = relationshipType ? STEPS[relationshipType] : [];
    const currentStepData = steps[currentStep];
    
    // Partner sharing functions
    const handleCreateSharedLoop = async () => {
        if (!myName.trim()) {
            toast.error('Please enter your name');
            return;
        }
        
        setIsSharing(true);
        try {
            const response = await fetch(`${API_URL}/api/relationship/infinity-loop/share`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    creator_loop: {
                        relationship_type: relationshipType,
                        answers: answers
                    },
                    creator_name: myName.trim()
                })
            });
            
            if (!response.ok) throw new Error('Failed to create shared session');
            
            const data = await response.json();
            setShareCode(data.share_code);
            setPhase('share-created');
            toast.success('Share code created!');
        } catch (error) {
            console.error('Share error:', error);
            toast.error('Failed to create share code', { description: 'Please try again.' });
        } finally {
            setIsSharing(false);
        }
    };
    
    const checkShareCode = async (code) => {
        try {
            const response = await fetch(`${API_URL}/api/relationship/infinity-loop/share/${code}`);
            if (!response.ok) {
                if (response.status === 404) {
                    toast.error('Invalid code', { description: 'Please check your share code and try again.' });
                    return;
                }
                throw new Error('Failed to check code');
            }
            
            const data = await response.json();
            setSharedSession(data);
            
            if (data.status === 'waiting') {
                // Partner can now complete their loop
                setPartnerName(data.creator_name);
                setPhase('join');
            } else if (data.status === 'both_complete' || data.status === 'viewed') {
                // Both complete - show comparison
                setPhase('comparison');
            }
        } catch (error) {
            console.error('Check code error:', error);
            toast.error('Failed to check share code');
        }
    };
    
    const handleJoinWithCode = () => {
        if (joinCode.length >= 4) {
            checkShareCode(joinCode.toUpperCase());
        } else {
            toast.error('Please enter a valid share code');
        }
    };
    
    const handleSubmitPartnerLoop = async () => {
        if (!myName.trim()) {
            toast.error('Please enter your name');
            return;
        }
        
        setIsSharing(true);
        try {
            const response = await fetch(`${API_URL}/api/relationship/infinity-loop/share/${joinCode}/join`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    partner_loop: {
                        relationship_type: relationshipType,
                        answers: answers
                    },
                    partner_name: myName.trim()
                })
            });
            
            if (!response.ok) throw new Error('Failed to submit loop');
            
            const data = await response.json();
            setSharedSession(data);
            setPhase('comparison');
            toast.success('Both loops complete!', { description: 'You can now see your patterns together.' });
        } catch (error) {
            console.error('Submit error:', error);
            toast.error('Failed to submit your loop', { description: 'Please try again.' });
        } finally {
            setIsSharing(false);
        }
    };
    
    const copyShareCode = () => {
        const shareUrl = `${window.location.origin}/relationship-hub/infinity-loop?code=${shareCode}`;
        navigator.clipboard.writeText(shareUrl).then(() => {
            setCodeCopied(true);
            toast.success('Link copied!', { description: 'Send this to your partner.' });
            setTimeout(() => setCodeCopied(false), 2000);
        });
    };
    
    const copyCodeOnly = () => {
        navigator.clipboard.writeText(shareCode).then(() => {
            setCodeCopied(true);
            toast.success('Code copied!');
            setTimeout(() => setCodeCopied(false), 2000);
        });
    };
    
    // Export as image and download
    const handleDownloadImage = async () => {
        if (!exportRef.current) return;
        
        setIsExporting(true);
        try {
            const dataUrl = await htmlToImage.toPng(exportRef.current, {
                quality: 1,
                backgroundColor: '#FBE0C3',
                pixelRatio: 2,
                style: {
                    transform: 'scale(1)',
                    transformOrigin: 'top left'
                }
            });
            
            // Create download link
            const link = document.createElement('a');
            const timestamp = new Date().toISOString().split('T')[0];
            link.download = `infinity-loop-${relationshipType}-${timestamp}.png`;
            link.href = dataUrl;
            link.click();
            
            toast.success('Image downloaded!', { description: 'Your Infinity Loop has been saved as an image.' });
        } catch (error) {
            console.error('Export failed:', error);
            toast.error('Export failed', { description: 'Unable to generate image. Please try again.' });
        } finally {
            setIsExporting(false);
        }
    };
    
    // Share using Web Share API (mobile-friendly)
    const handleShare = async () => {
        if (!exportRef.current) return;
        
        setIsExporting(true);
        try {
            const dataUrl = await htmlToImage.toPng(exportRef.current, {
                quality: 1,
                backgroundColor: '#FBE0C3',
                pixelRatio: 2
            });
            
            // Convert data URL to blob
            const response = await fetch(dataUrl);
            const blob = await response.blob();
            const file = new File([blob], 'infinity-loop.png', { type: 'image/png' });
            
            if (navigator.share && navigator.canShare({ files: [file] })) {
                await navigator.share({
                    title: 'My Infinity Loop',
                    text: `I completed an Infinity Loop exercise to understand my ${relationshipType} relationship patterns. This is based on Emotionally Focused Therapy.`,
                    files: [file]
                });
                toast.success('Shared successfully!');
            } else if (navigator.share) {
                // Share without file (fallback)
                await navigator.share({
                    title: 'My Infinity Loop',
                    text: `I completed an Infinity Loop exercise to understand my ${relationshipType} relationship patterns using CoRegulate. The Infinity Loop is based on Emotionally Focused Therapy and helps identify negative cycles in relationships.`,
                    url: window.location.href
                });
                toast.success('Shared successfully!');
            } else {
                // Fallback: copy to clipboard
                handleCopyText();
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error('Share failed:', error);
                toast.error('Share failed', { description: 'Unable to share. Try downloading instead.' });
            }
        } finally {
            setIsExporting(false);
        }
    };
    
    // Copy loop content as text
    const handleCopyText = () => {
        const textContent = generateTextSummary();
        navigator.clipboard.writeText(textContent).then(() => {
            setCopied(true);
            toast.success('Copied to clipboard!', { description: 'You can paste this in a message or document.' });
            setTimeout(() => setCopied(false), 2000);
        }).catch(() => {
            toast.error('Copy failed', { description: 'Unable to copy to clipboard.' });
        });
    };
    
    // Generate text summary of the loop
    const generateTextSummary = () => {
        const typeLabel = relationshipType === 'partner' ? 'Partner' : 'Family';
        const currentSteps = STEPS[relationshipType] || [];
        
        let text = `🔄 MY INFINITY LOOP (${typeLabel} Relationship)\n`;
        text += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;
        text += `📍 UNDER THE SURFACE (Internal)\n\n`;
        
        currentSteps.forEach(step => {
            if (step.zone === 'under') {
                text += `${step.id}. ${step.title}\n`;
                text += `"${step.prompt}"\n`;
                text += `→ ${answers[step.id] || '(No answer)'}\n\n`;
            }
        });
        
        text += `📍 ABOVE THE SURFACE (Expressed)\n\n`;
        
        currentSteps.forEach(step => {
            if (step.zone === 'above') {
                text += `${step.id}. ${step.title}\n`;
                text += `"${step.prompt}"\n`;
                text += `→ ${answers[step.id] || '(No answer)'}\n\n`;
            }
        });
        
        text += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
        text += `Created with CoRegulate - coregulateadhd.com\n`;
        text += `Based on Emotionally Focused Therapy (EFT)`;
        
        return text;
    };
    
    const handleTypeSelect = (type) => {
        setRelationshipType(type);
        setPhase('step');
        setCurrentStep(0);
        setAnswers({});
    };
    
    const handleNext = () => {
        if (currentInput.trim()) {
            const newAnswers = { ...answers, [currentStepData.id]: currentInput.trim() };
            setAnswers(newAnswers);
            setCurrentInput('');
            setShowExample(false);
            
            if (currentStep < steps.length - 1) {
                setFadeIn(false);
                setTimeout(() => {
                    setCurrentStep(currentStep + 1);
                    setFadeIn(true);
                }, 200);
            } else {
                setPhase('review');
            }
        }
    };
    
    const handleBack = () => {
        if (currentStep > 0) {
            setFadeIn(false);
            setTimeout(() => {
                const prevStep = steps[currentStep - 1];
                setCurrentInput(answers[prevStep.id] || '');
                setCurrentStep(currentStep - 1);
                setFadeIn(true);
            }, 200);
        } else if (phase === 'step') {
            setPhase('type-select');
        } else if (phase === 'type-select') {
            setPhase('intro');
        }
    };
    
    const handleSave = () => {
        // If we're in a join flow (partner completing their loop), submit to server
        if (joinCode && sharedSession) {
            handleSubmitPartnerLoop();
            return;
        }
        
        // Otherwise, save locally
        const newLoop = {
            id: Date.now(),
            type: relationshipType,
            answers,
            createdAt: new Date().toISOString()
        };
        const updated = [newLoop, ...savedLoops].slice(0, 10); // Keep max 10
        setSavedLoops(updated);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
        setPhase('complete');
    };
    
    const handleStartNew = () => {
        setPhase('type-select');
        setRelationshipType(null);
        setCurrentStep(0);
        setAnswers({});
        setCurrentInput('');
    };
    
    const handleBackNavigation = () => {
        if (phase === 'intro') {
            navigate('/relationship-hub');
        } else if (phase === 'type-select') {
            setPhase('intro');
        } else if (phase === 'step') {
            handleBack();
        } else if (phase === 'review') {
            setPhase('step');
            setCurrentStep(steps.length - 1);
            setCurrentInput(answers[steps[steps.length - 1].id] || '');
        } else if (phase === 'complete') {
            handleStartNew();
        }
    };
    
    return (
        <div className="min-h-screen bg-transparent">
            <Helmet>
                <title>Infinity Loop - Identify Negative Cycles | CoRegulate</title>
                <meta name="description" content="Use the Infinity Loop tool from Emotionally Focused Therapy to identify negative cycles in your relationships and break patterns that no longer serve you." />
            </Helmet>
            
            <ModuleHeader
                title="Infinity Loop"
                onBack={handleBackNavigation}
                backDestination="/relationship-hub"
                exitDestination="/home?tab=relationships"
                showBack={true}
            />
            
            <main className="max-w-xl mx-auto px-6 py-6">
                {/* INTRO PHASE */}
                {phase === 'intro' && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-6"
                    >
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#1D6B6B] to-[#FFBB98] flex items-center justify-center">
                                <RefreshCw className="w-8 h-8 text-white" />
                            </div>
                            <Heading as="h1" className="text-[#1D6B6B] mb-3">
                                The Infinity Loop
                            </Heading>
                            <BodyText className="text-[#7D8E95]">
                                Identify negative cycles in your relationships
                            </BodyText>
                        </div>
                        
                        <div className="glass-card p-6 space-y-4">
                            <BodyText className="text-[#5A6A6A] leading-relaxed">
                                Do you ever feel like you go through the same cycles in your relationships? 
                                Do you and your partner find yourselves arguing over the same issues, time and time again?
                            </BodyText>
                            
                            <BodyText className="text-[#5A6A6A] leading-relaxed">
                                The Infinity Loop is part of <strong>Emotionally Focused Therapy</strong>, an evidence-based 
                                approach used in couple's therapy. Dr. Scott Woolley first introduced this tool to help 
                                couples identify negative patterns that perpetuate undesired outcomes.
                            </BodyText>
                            
                            <BodyText className="text-[#5A6A6A] leading-relaxed">
                                By identifying and communicating these cycles, each person gains a deeper understanding 
                                of one another's experience—leading to less argumentative and more effective conversations.
                            </BodyText>
                        </div>
                        
                        {/* Simple infinity visualization */}
                        <div className="py-4">
                            <svg viewBox="0 0 200 80" className="w-full max-w-xs mx-auto h-auto opacity-60">
                                <path
                                    d="M100,40 C100,20 60,10 60,40 C60,70 100,60 100,40 C100,20 140,10 140,40 C140,70 100,60 100,40"
                                    fill="none"
                                    stroke="url(#introGradient)"
                                    strokeWidth="3"
                                />
                                <defs>
                                    <linearGradient id="introGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                        <stop offset="0%" stopColor="#1D6B6B" />
                                        <stop offset="50%" stopColor="#FFBB98" />
                                        <stop offset="100%" stopColor="#1D6B6B" />
                                    </linearGradient>
                                </defs>
                            </svg>
                        </div>
                        
                        <div className="glass-card-sage p-4">
                            <SmallText className="text-[#1D6B6B] leading-relaxed">
                                <strong>How it works:</strong> You'll answer 6 questions that explore what happens 
                                "under the surface" (your internal experience) and "above the surface" (what you express). 
                                This helps reveal the cycle that keeps repeating.
                            </SmallText>
                        </div>
                        
                        <PrimaryButton 
                            onClick={() => setPhase('type-select')}
                            className="w-full"
                            data-testid="start-infinity-loop"
                        >
                            Begin the Infinity Loop
                        </PrimaryButton>
                        
                        {/* Join with code section */}
                        <div className="glass-card p-4">
                            <SmallText className="text-[#7D8E95] mb-3 block text-center">
                                Have a partner code?
                            </SmallText>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    placeholder="Enter code"
                                    value={joinCode}
                                    onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                                    maxLength={6}
                                    className="flex-1 px-4 py-2 rounded-xl bg-white/80 border border-[#E8E4DC] 
                                             focus:border-[#1D6B6B] focus:ring-1 focus:ring-[#1D6B6B] 
                                             outline-none text-[#2A2520] placeholder-[#7D8E95] text-center font-mono tracking-widest uppercase"
                                />
                                <SecondaryButton
                                    onClick={handleJoinWithCode}
                                    disabled={joinCode.length < 4}
                                    className="px-4"
                                    data-testid="join-with-code"
                                >
                                    Join
                                </SecondaryButton>
                            </div>
                        </div>
                        
                        {savedLoops.length > 0 && (
                            <button
                                onClick={() => setPhase('review')}
                                className="w-full text-center text-[#1D6B6B] text-sm hover:underline"
                            >
                                View my previous loops ({savedLoops.length})
                            </button>
                        )}
                        
                        <BottomBarSpacer />
                    </motion.div>
                )}
                
                {/* TYPE SELECT PHASE */}
                {phase === 'type-select' && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-6"
                    >
                        <div className="text-center mb-8">
                            <Heading as="h2" className="text-[#1D6B6B] mb-3">
                                Which relationship?
                            </Heading>
                            <BodyText className="text-[#7D8E95]">
                                Choose the type of relationship you'd like to explore
                            </BodyText>
                        </div>
                        
                        <button
                            onClick={() => handleTypeSelect('partner')}
                            className="w-full glass-card-rose p-6 text-left hover:scale-[1.02] transition-transform"
                            data-testid="select-partner"
                        >
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FFBB98] to-[#E09060] flex items-center justify-center">
                                    <Heart className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <Heading as="h3" size="text-lg" className="text-[#1D6B6B]">
                                        Partner Relationship
                                    </Heading>
                                    <SmallText className="text-[#7D8E95]">
                                        Romantic partner, spouse, significant other
                                    </SmallText>
                                </div>
                            </div>
                        </button>
                        
                        <button
                            onClick={() => handleTypeSelect('family')}
                            className="w-full glass-card-lavender p-6 text-left hover:scale-[1.02] transition-transform"
                            data-testid="select-family"
                        >
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#7D8E95] to-[#5A6A6A] flex items-center justify-center">
                                    <Users className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <Heading as="h3" size="text-lg" className="text-[#1D6B6B]">
                                        Family Relationship
                                    </Heading>
                                    <SmallText className="text-[#7D8E95]">
                                        Parent-child, sibling, or other family member
                                    </SmallText>
                                </div>
                            </div>
                        </button>
                        
                        <BottomBarSpacer />
                    </motion.div>
                )}
                
                {/* STEP PHASE */}
                {phase === 'step' && currentStepData && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: fadeIn ? 1 : 0 }}
                        transition={{ duration: 0.2 }}
                        className="space-y-6"
                    >
                        {/* Progress and visualization */}
                        <InfinityLoopVisualization 
                            currentStep={currentStep}
                            answers={answers}
                            relationshipType={relationshipType}
                        />
                        
                        {/* Zone indicator */}
                        <div className="text-center">
                            <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                                currentStepData.zone === 'under' 
                                    ? 'bg-[#1D6B6B]/20 text-[#1D6B6B]' 
                                    : 'bg-[#FFBB98]/20 text-[#FFBB98]'
                            }`}>
                                {currentStepData.zone === 'under' ? 'Under the Surface (Internal)' : 'Above the Surface (Expressed)'}
                            </span>
                        </div>
                        
                        {/* Step content */}
                        <div className="glass-card p-6 space-y-4">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-10 h-10 rounded-full bg-[#1D6B6B] flex items-center justify-center">
                                    <span className="text-white font-bold">{currentStepData.id}</span>
                                </div>
                                <Heading as="h3" size="text-lg" className="text-[#1D6B6B]">
                                    {currentStepData.title}
                                </Heading>
                            </div>
                            
                            <div className="bg-[#1D6B6B]/10 rounded-lg p-3">
                                <BodyText className="text-[#1D6B6B] italic">
                                    {currentStepData.prompt}
                                </BodyText>
                            </div>
                            
                            <BodyText className="text-[#5A6A6A]">
                                {currentStepData.question}
                            </BodyText>
                            
                            {/* Example toggle */}
                            <button
                                onClick={() => setShowExample(!showExample)}
                                className="flex items-center gap-2 text-sm text-[#7D8E95] hover:text-[#1D6B6B] transition-colors"
                            >
                                {showExample ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                {showExample ? 'Hide example' : 'Show example'}
                            </button>
                            
                            <AnimatePresence>
                                {showExample && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="bg-[#FFBB98]/10 rounded-lg p-3"
                                    >
                                        <SmallText className="text-[#7D8E95] italic">
                                            Example: "{currentStepData.example}"
                                        </SmallText>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                            
                            {/* Input area */}
                            <textarea
                                value={currentInput}
                                onChange={(e) => setCurrentInput(e.target.value)}
                                placeholder="Type your answer here..."
                                className="w-full h-32 p-4 rounded-xl bg-white/80 border border-[#E8E4DC] 
                                         focus:border-[#1D6B6B] focus:ring-1 focus:ring-[#1D6B6B] 
                                         outline-none resize-none text-[#2A2520] placeholder-[#7D8E95]"
                                data-testid={`step-${currentStepData.id}-input`}
                            />
                        </div>
                        
                        {/* Navigation */}
                        <div className="flex gap-3">
                            <SecondaryButton
                                onClick={handleBack}
                                className="flex-1"
                            >
                                <ChevronLeft className="w-4 h-4 mr-1" />
                                Back
                            </SecondaryButton>
                            <PrimaryButton
                                onClick={handleNext}
                                disabled={!currentInput.trim()}
                                className="flex-1"
                                data-testid="next-step"
                            >
                                {currentStep < steps.length - 1 ? 'Next' : 'Review'}
                                <ChevronRight className="w-4 h-4 ml-1" />
                            </PrimaryButton>
                        </div>
                        
                        <BottomBarSpacer />
                    </motion.div>
                )}
                
                {/* REVIEW PHASE */}
                {phase === 'review' && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-6"
                    >
                        <div className="text-center mb-6">
                            <Heading as="h2" className="text-[#1D6B6B] mb-2">
                                Your Infinity Loop
                            </Heading>
                            <BodyText className="text-[#7D8E95]">
                                Review your cycle and see the pattern
                            </BodyText>
                        </div>
                        
                        {/* Exportable content wrapper */}
                        <div ref={exportRef} className="space-y-6 p-4 -m-4 bg-[#FBE0C3] rounded-2xl">
                            {/* Header for export */}
                            <div className="text-center pb-2">
                                <SmallText className="text-[#7D8E95] font-medium uppercase tracking-wide">
                                    {relationshipType === 'partner' ? 'Partner' : 'Family'} Relationship
                                </SmallText>
                            </div>
                            
                            {/* Visualization */}
                            <InfinityLoopVisualization 
                                currentStep={-1}
                                answers={answers}
                                relationshipType={relationshipType}
                            />
                            
                            {/* Answers review */}
                            <div className="space-y-4">
                                {steps.map((step, index) => (
                                    <div 
                                        key={step.id}
                                        className={`glass-card p-4 ${
                                            step.zone === 'under' ? 'border-l-4 border-[#1D6B6B]' : 'border-l-4 border-[#FFBB98]'
                                        }`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center ${
                                                step.zone === 'under' ? 'bg-[#1D6B6B]' : 'bg-[#FFBB98]'
                                            }`}>
                                                <span className="text-white font-bold text-sm">{step.id}</span>
                                            </div>
                                            <div>
                                                <SmallText className="text-[#7D8E95] font-medium">
                                                    {step.title}
                                                </SmallText>
                                                <BodyText className="text-[#2A2520] mt-1">
                                                    {answers[step.id] || '(No answer)'}
                                                </BodyText>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            
                            {/* Footer for export */}
                            <div className="text-center pt-2 opacity-60">
                                <SmallText className="text-[#7D8E95]">
                                    Created with CoRegulate • coregulateadhd.com
                                </SmallText>
                            </div>
                        </div>
                        
                        {/* Insight card (outside export area) */}
                        <div className="glass-card-sage p-5">
                            <div className="flex items-start gap-3">
                                <Sparkles className="w-5 h-5 text-[#1D6B6B] flex-shrink-0 mt-0.5" />
                                <div>
                                    <Heading as="h4" size="text-base" className="text-[#1D6B6B] mb-2">
                                        Breaking the Cycle
                                    </Heading>
                                    <BodyText className="text-[#5A6A6A] text-sm leading-relaxed">
                                        The Infinity Loop is most effective when shared with the other person. 
                                        It gives them a better understanding of what's happening underneath. 
                                        You can also learn about their feelings and needs, opening up communication 
                                        to understand one another better and work together to improve your relationship.
                                    </BodyText>
                                </div>
                            </div>
                        </div>
                        
                        {/* Export buttons */}
                        <div className="flex gap-2 justify-center">
                            <button
                                onClick={handleDownloadImage}
                                disabled={isExporting}
                                className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/60 hover:bg-white/80 
                                         text-[#1D6B6B] text-sm font-medium transition-all border border-[#1D6B6B]/20
                                         disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid="download-image-btn"
                            >
                                {isExporting ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Download className="w-4 h-4" />
                                )}
                                Download
                            </button>
                            <button
                                onClick={handleShare}
                                disabled={isExporting}
                                className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/60 hover:bg-white/80 
                                         text-[#1D6B6B] text-sm font-medium transition-all border border-[#1D6B6B]/20
                                         disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid="share-btn"
                            >
                                <Share2 className="w-4 h-4" />
                                Share
                            </button>
                            <button
                                onClick={handleCopyText}
                                className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/60 hover:bg-white/80 
                                         text-[#1D6B6B] text-sm font-medium transition-all border border-[#1D6B6B]/20"
                                data-testid="copy-text-btn"
                            >
                                {copied ? (
                                    <Check className="w-4 h-4 text-green-600" />
                                ) : (
                                    <Copy className="w-4 h-4" />
                                )}
                                {copied ? 'Copied!' : 'Copy Text'}
                            </button>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex gap-3">
                            <SecondaryButton
                                onClick={() => {
                                    setPhase('step');
                                    setCurrentStep(0);
                                    setCurrentInput(answers['A'] || '');
                                }}
                                className="flex-1"
                            >
                                Edit Answers
                            </SecondaryButton>
                            <PrimaryButton
                                onClick={handleSave}
                                disabled={isSharing}
                                className="flex-1"
                                data-testid="save-loop"
                            >
                                {isSharing ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : joinCode && sharedSession ? (
                                    <Users className="w-4 h-4 mr-2" />
                                ) : (
                                    <Save className="w-4 h-4 mr-2" />
                                )}
                                {joinCode && sharedSession ? 'Submit & Compare' : 'Save Loop'}
                            </PrimaryButton>
                        </div>
                        
                        <BottomBarSpacer />
                    </motion.div>
                )}
                
                {/* COMPLETE PHASE */}
                {phase === 'complete' && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-6 text-center"
                    >
                        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#1D6B6B] to-[#FFBB98] flex items-center justify-center">
                            <Sparkles className="w-10 h-10 text-white" />
                        </div>
                        
                        <Heading as="h2" className="text-[#1D6B6B]">
                            Loop Saved
                        </Heading>
                        
                        <BodyText className="text-[#7D8E95] max-w-sm mx-auto">
                            You've taken an important step in understanding your relationship patterns. 
                            Consider sharing this with your {relationshipType === 'partner' ? 'partner' : 'family member'} 
                            to start a meaningful conversation.
                        </BodyText>
                        
                        {/* Share/Export options */}
                        <div className="glass-card p-5">
                            <Heading as="h4" size="text-base" className="text-[#1D6B6B] mb-4">
                                Share Your Loop
                            </Heading>
                            <div className="flex gap-2 justify-center flex-wrap">
                                <button
                                    onClick={handleDownloadImage}
                                    disabled={isExporting}
                                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#1D6B6B]/10 hover:bg-[#1D6B6B]/20 
                                             text-[#1D6B6B] text-sm font-medium transition-all
                                             disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid="complete-download-btn"
                                >
                                    {isExporting ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Download className="w-4 h-4" />
                                    )}
                                    Save Image
                                </button>
                                <button
                                    onClick={handleShare}
                                    disabled={isExporting}
                                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#1D6B6B]/10 hover:bg-[#1D6B6B]/20 
                                             text-[#1D6B6B] text-sm font-medium transition-all
                                             disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid="complete-share-btn"
                                >
                                    <Share2 className="w-4 h-4" />
                                    Share
                                </button>
                                <button
                                    onClick={handleCopyText}
                                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#1D6B6B]/10 hover:bg-[#1D6B6B]/20 
                                             text-[#1D6B6B] text-sm font-medium transition-all"
                                    data-testid="complete-copy-btn"
                                >
                                    {copied ? (
                                        <Check className="w-4 h-4 text-green-600" />
                                    ) : (
                                        <Copy className="w-4 h-4" />
                                    )}
                                    {copied ? 'Copied!' : 'Copy Text'}
                                </button>
                            </div>
                            <SmallText className="text-[#7D8E95] mt-3 block">
                                Bring your loop to therapy sessions or share with your {relationshipType === 'partner' ? 'partner' : 'family member'}
                            </SmallText>
                        </div>
                        
                        {/* Solo Exploration Insights */}
                        <div className="glass-card p-5">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1D6B6B] to-[#FFBB98] flex items-center justify-center">
                                    <Sparkles className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <Heading as="h4" size="text-base" className="text-[#1D6B6B]">
                                        Your Pattern Insights
                                    </Heading>
                                    <SmallText className="text-[#7D8E95]">
                                        What your loop reveals about you
                                    </SmallText>
                                </div>
                            </div>
                            
                            <div className="space-y-4 text-sm">
                                {/* Under the surface insight */}
                                <div className="bg-[#1D6B6B]/10 rounded-xl p-4">
                                    <SmallText className="text-[#1D6B6B] font-semibold mb-2 block">
                                        Under the Surface
                                    </SmallText>
                                    <BodyText className="text-[#5A6A6A] text-sm leading-relaxed">
                                        Your core need is <span className="font-medium text-[#1D6B6B]">"{answers['A']}"</span> — 
                                        and when that's not met, you feel <span className="font-medium text-[#1D6B6B]">"{answers['B']}"</span>. 
                                        This vulnerable feeling is at the heart of the cycle.
                                    </BodyText>
                                </div>
                                
                                {/* Above the surface insight */}
                                <div className="bg-[#FFBB98]/10 rounded-xl p-4">
                                    <SmallText className="text-[#FFBB98] font-semibold mb-2 block">
                                        Above the Surface
                                    </SmallText>
                                    <BodyText className="text-[#5A6A6A] text-sm leading-relaxed">
                                        Instead of showing that vulnerability, you <span className="font-medium text-[#FFBB98]">"{answers['C']}"</span>. 
                                        This protective response makes sense, but it can trigger the other person and keep the cycle going.
                                    </BodyText>
                                </div>
                                
                                {/* The cycle insight */}
                                <div className="bg-white/50 rounded-xl p-4 border border-[#E8E4DC]">
                                    <SmallText className="text-[#7D8E95] font-semibold mb-2 block">
                                        The Cycle Connection
                                    </SmallText>
                                    <BodyText className="text-[#5A6A6A] text-sm leading-relaxed">
                                        When you <span className="font-medium">"{answers['F']}"</span>, it likely triggers the other person's 
                                        own vulnerable feelings — and their protective response then triggers YOUR need for "{answers['A']}". 
                                        This is how the loop keeps spinning.
                                    </BodyText>
                                </div>
                            </div>
                        </div>
                        
                        {/* Reflection Questions for Solo Users */}
                        <div className="glass-card-sage p-5">
                            <Heading as="h4" size="text-base" className="text-[#1D6B6B] mb-3">
                                Questions to Reflect On
                            </Heading>
                            <ul className="space-y-3 text-[#5A6A6A] text-sm">
                                <li className="flex items-start gap-2">
                                    <span className="text-[#1D6B6B] font-bold">•</span>
                                    What would happen if you shared your vulnerable feeling (Step B) directly, instead of showing Step C?
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-[#1D6B6B] font-bold">•</span>
                                    Can you recognize the moment when you shift from feeling vulnerable to becoming protective?
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-[#1D6B6B] font-bold">•</span>
                                    What might the other person be yearning for? What vulnerable feelings might they be hiding?
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-[#1D6B6B] font-bold">•</span>
                                    How might you interrupt your Step F before it triggers the other person?
                                </li>
                            </ul>
                        </div>
                        
                        {/* Partner Sharing Section - Optional */}
                        {relationshipType === 'partner' && (
                            <div className="glass-card p-5">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 rounded-full bg-[#1D6B6B] flex items-center justify-center">
                                        <Users className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                        <Heading as="h4" size="text-base" className="text-[#1D6B6B]">
                                            Want to Go Deeper?
                                        </Heading>
                                        <SmallText className="text-[#7D8E95]">
                                            Invite your partner to complete their own loop
                                        </SmallText>
                                    </div>
                                </div>
                                
                                <BodyText className="text-[#5A6A6A] text-sm mb-4">
                                    The Infinity Loop is most powerful when both people can see each other's experience. 
                                    Your partner can complete their own loop, and then you can view them side-by-side.
                                </BodyText>
                                
                                <div className="space-y-3">
                                    <input
                                        type="text"
                                        placeholder="Your name (e.g., Sarah)"
                                        value={myName}
                                        onChange={(e) => setMyName(e.target.value)}
                                        className="w-full px-4 py-3 rounded-xl bg-white/80 border border-[#E8E4DC] 
                                                 focus:border-[#1D6B6B] focus:ring-1 focus:ring-[#1D6B6B] 
                                                 outline-none text-[#2A2520] placeholder-[#7D8E95]"
                                    />
                                    <SecondaryButton
                                        onClick={handleCreateSharedLoop}
                                        disabled={isSharing || !myName.trim()}
                                        className="w-full"
                                        data-testid="create-partner-share"
                                    >
                                        {isSharing ? (
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        ) : (
                                            <Link2 className="w-4 h-4 mr-2" />
                                        )}
                                        Create Link for Partner
                                    </SecondaryButton>
                                </div>
                            </div>
                        )}
                        
                        {/* Family Sharing Section - Optional */}
                        {relationshipType === 'family' && (
                            <div className="glass-card p-5">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 rounded-full bg-[#1D6B6B] flex items-center justify-center">
                                        <Users className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                        <Heading as="h4" size="text-base" className="text-[#1D6B6B]">
                                            Want to Go Deeper?
                                        </Heading>
                                        <SmallText className="text-[#7D8E95]">
                                            Invite your family member to complete their own loop
                                        </SmallText>
                                    </div>
                                </div>
                                
                                <BodyText className="text-[#5A6A6A] text-sm mb-4">
                                    If your child is old enough (teens and up), or if this is with another adult family member, 
                                    they can complete their own loop. Seeing both perspectives can be incredibly powerful for healing.
                                </BodyText>
                                
                                <div className="space-y-3">
                                    <input
                                        type="text"
                                        placeholder="Your name (e.g., Mom, Dad)"
                                        value={myName}
                                        onChange={(e) => setMyName(e.target.value)}
                                        className="w-full px-4 py-3 rounded-xl bg-white/80 border border-[#E8E4DC] 
                                                 focus:border-[#1D6B6B] focus:ring-1 focus:ring-[#1D6B6B] 
                                                 outline-none text-[#2A2520] placeholder-[#7D8E95]"
                                    />
                                    <SecondaryButton
                                        onClick={handleCreateSharedLoop}
                                        disabled={isSharing || !myName.trim()}
                                        className="w-full"
                                        data-testid="create-family-share"
                                    >
                                        {isSharing ? (
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        ) : (
                                            <Link2 className="w-4 h-4 mr-2" />
                                        )}
                                        Create Link for Family Member
                                    </SecondaryButton>
                                </div>
                            </div>
                        )}
                        
                        <div className="glass-card p-5 text-left">
                            <Heading as="h4" size="text-base" className="text-[#1D6B6B] mb-3">
                                Using Your Loop
                            </Heading>
                            <ul className="space-y-2 text-[#5A6A6A] text-sm">
                                <li className="flex items-start gap-2">
                                    <span className="text-[#1D6B6B]">1.</span>
                                    Notice when you're entering the cycle — catch yourself at Step C or D
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-[#1D6B6B]">2.</span>
                                    Try expressing Step B (your vulnerable feeling) instead of Step C
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-[#1D6B6B]">3.</span>
                                    Remember: the other person has their own loop — you're both caught in a dance
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-[#1D6B6B]">4.</span>
                                    Consider sharing this with a therapist or the other person when the time feels right
                                </li>
                            </ul>
                        </div>
                        
                        <div className="space-y-3">
                            <PrimaryButton
                                onClick={handleStartNew}
                                className="w-full"
                            >
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Start Another Loop
                            </PrimaryButton>
                            
                            <SecondaryButton
                                onClick={() => navigate('/relationship-hub')}
                                className="w-full"
                            >
                                <Home className="w-4 h-4 mr-2" />
                                Back to Relationship Hub
                            </SecondaryButton>
                        </div>
                        
                        <BottomBarSpacer />
                    </motion.div>
                )}
                
                {/* SHARE CREATED PHASE */}
                {phase === 'share-created' && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-6 text-center"
                    >
                        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#1D6B6B] to-[#FFBB98] flex items-center justify-center">
                            <Link2 className="w-10 h-10 text-white" />
                        </div>
                        
                        <Heading as="h2" className="text-[#1D6B6B]">
                            Share Code Created!
                        </Heading>
                        
                        <BodyText className="text-[#7D8E95] max-w-sm mx-auto">
                            Send this link or code to your partner so they can complete their own Infinity Loop.
                        </BodyText>
                        
                        {/* Share Code Display */}
                        <div className="glass-card p-6">
                            <SmallText className="text-[#7D8E95] mb-2 block">Your share code</SmallText>
                            <div className="text-4xl font-bold text-[#1D6B6B] tracking-widest mb-4">
                                {shareCode}
                            </div>
                            
                            <div className="flex gap-2 justify-center flex-wrap">
                                <button
                                    onClick={copyShareCode}
                                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#1D6B6B] text-white text-sm font-medium"
                                >
                                    {codeCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                    {codeCopied ? 'Copied!' : 'Copy Link'}
                                </button>
                                <button
                                    onClick={copyCodeOnly}
                                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#1D6B6B]/10 text-[#1D6B6B] text-sm font-medium"
                                >
                                    <Copy className="w-4 h-4" />
                                    Copy Code Only
                                </button>
                            </div>
                        </div>
                        
                        <div className="glass-card-sage p-5 text-left">
                            <Heading as="h4" size="text-base" className="text-[#1D6B6B] mb-3">
                                What happens next?
                            </Heading>
                            <ul className="space-y-2 text-[#5A6A6A] text-sm">
                                <li className="flex items-start gap-2">
                                    <span className="text-[#1D6B6B]">1.</span>
                                    Your partner opens the link and completes their loop
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-[#1D6B6B]">2.</span>
                                    Once they're done, you can both view the comparison
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-[#1D6B6B]">3.</span>
                                    Use the code to check status anytime
                                </li>
                            </ul>
                        </div>
                        
                        <div className="space-y-3">
                            <SecondaryButton
                                onClick={() => {
                                    setJoinCode(shareCode);
                                    checkShareCode(shareCode);
                                }}
                                className="w-full"
                            >
                                Check If Partner Completed
                            </SecondaryButton>
                            
                            <TertiaryButton
                                onClick={() => navigate('/relationship-hub')}
                                className="w-full text-[#7D8E95]"
                            >
                                Back to Relationship Hub
                            </TertiaryButton>
                        </div>
                        
                        <BottomBarSpacer />
                    </motion.div>
                )}
                
                {/* JOIN PHASE - Partner received code and can start their loop */}
                {phase === 'join' && sharedSession && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-6"
                    >
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#1D6B6B] to-[#FFBB98] flex items-center justify-center">
                                <UserPlus className="w-8 h-8 text-white" />
                            </div>
                            <Heading as="h1" className="text-[#1D6B6B] mb-3">
                                Complete Your Loop
                            </Heading>
                            <BodyText className="text-[#7D8E95]">
                                <strong>{sharedSession.creator_name}</strong> has invited you to complete your own Infinity Loop
                            </BodyText>
                        </div>
                        
                        <div className="glass-card p-5">
                            <SmallText className="text-[#7D8E95] mb-2">Your name</SmallText>
                            <input
                                type="text"
                                placeholder="Enter your name"
                                value={myName}
                                onChange={(e) => setMyName(e.target.value)}
                                className="w-full px-4 py-3 rounded-xl bg-white/80 border border-[#E8E4DC] 
                                         focus:border-[#1D6B6B] focus:ring-1 focus:ring-[#1D6B6B] 
                                         outline-none text-[#2A2520] placeholder-[#7D8E95]"
                            />
                        </div>
                        
                        <div className="glass-card-sage p-4">
                            <SmallText className="text-[#1D6B6B] leading-relaxed">
                                You'll answer 6 questions about your experience in the relationship. 
                                {sharedSession.creator_name} won't see your answers until you both complete your loops, 
                                then you can view them together.
                            </SmallText>
                        </div>
                        
                        <PrimaryButton 
                            onClick={() => {
                                if (!myName.trim()) {
                                    toast.error('Please enter your name');
                                    return;
                                }
                                setRelationshipType(sharedSession.creator_relationship_type || 'partner');
                                setPhase('step');
                                setCurrentStep(0);
                                setAnswers({});
                            }}
                            disabled={!myName.trim()}
                            className="w-full"
                            data-testid="start-partner-loop"
                        >
                            Begin My Infinity Loop
                            <ChevronRight className="w-4 h-4 ml-1" />
                        </PrimaryButton>
                        
                        <BottomBarSpacer />
                    </motion.div>
                )}
                
                {/* COMPARISON PHASE - Both loops complete */}
                {phase === 'comparison' && sharedSession && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-6"
                    >
                        <div className="text-center mb-6">
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#1D6B6B] to-[#FFBB98] flex items-center justify-center">
                                <Users className="w-8 h-8 text-white" />
                            </div>
                            <Heading as="h2" className="text-[#1D6B6B] mb-2">
                                Your Loops Together
                            </Heading>
                            <BodyText className="text-[#7D8E95]">
                                See how your patterns connect
                            </BodyText>
                        </div>
                        
                        {/* Side by side comparison */}
                        <div className="space-y-6">
                            {STEPS[sharedSession.creator_loop?.relationship_type || 'partner'].map((step) => (
                                <div key={step.id} className="glass-card p-4">
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                            step.zone === 'under' ? 'bg-[#1D6B6B]' : 'bg-[#FFBB98]'
                                        }`}>
                                            <span className="text-white font-bold text-sm">{step.id}</span>
                                        </div>
                                        <div>
                                            <SmallText className="font-semibold text-[#1D6B6B]">{step.title}</SmallText>
                                            <SmallText className="text-[#7D8E95] text-xs">
                                                {step.zone === 'under' ? 'Under the Surface' : 'Above the Surface'}
                                            </SmallText>
                                        </div>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {/* Creator's answer */}
                                        <div className="bg-[#1D6B6B]/10 rounded-xl p-3">
                                            <SmallText className="text-[#1D6B6B] font-medium mb-1 block">
                                                {sharedSession.creator_name}
                                            </SmallText>
                                            <BodyText className="text-[#2A2520] text-sm">
                                                {sharedSession.creator_loop?.answers?.[step.id] || '(No answer)'}
                                            </BodyText>
                                        </div>
                                        
                                        {/* Partner's answer */}
                                        <div className="bg-[#FFBB98]/10 rounded-xl p-3">
                                            <SmallText className="text-[#FFBB98] font-medium mb-1 block">
                                                {sharedSession.partner_name}
                                            </SmallText>
                                            <BodyText className="text-[#2A2520] text-sm">
                                                {sharedSession.partner_loop?.answers?.[step.id] || '(No answer)'}
                                            </BodyText>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        
                        {/* Insight */}
                        <div className="glass-card-sage p-5">
                            <div className="flex items-start gap-3">
                                <Sparkles className="w-5 h-5 text-[#1D6B6B] flex-shrink-0 mt-0.5" />
                                <div>
                                    <Heading as="h4" size="text-base" className="text-[#1D6B6B] mb-2">
                                        Understanding Your Cycle
                                    </Heading>
                                    <BodyText className="text-[#5A6A6A] text-sm leading-relaxed">
                                        Notice how your triggers and responses might connect. When one of you does Step F, 
                                        it may trigger the other's Step A - creating the loop. Understanding this can help 
                                        you interrupt the pattern together.
                                    </BodyText>
                                </div>
                            </div>
                        </div>
                        
                        <div className="space-y-3">
                            <PrimaryButton
                                onClick={handleStartNew}
                                className="w-full"
                            >
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Start a New Loop
                            </PrimaryButton>
                            
                            <SecondaryButton
                                onClick={() => navigate('/relationship-hub')}
                                className="w-full"
                            >
                                <Home className="w-4 h-4 mr-2" />
                                Back to Relationship Hub
                            </SecondaryButton>
                        </div>
                        
                        <BottomBarSpacer />
                    </motion.div>
                )}
            </main>
        </div>
    );
};

export default InfinityLoop;
